import React, { useState, useCallback } from 'react';
import { Product } from '../types';
import { useCart } from '../context/CartContext';
import Toast from '../components/Toast';

interface ProductDetailProps {
  product: Product;
  onNavigate: (page: string) => void;
}

const ProductDetail: React.FC<ProductDetailProps> = ({ product, onNavigate }) => {
  const { addToCart, cart } = useCart();
  const [activeMedia, setActiveMedia] = useState<'video' | number>(product.video ? 'video' : 0);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);

  const showToast = useCallback((message: string, type: 'success' | 'error' | 'info' = 'success') => {
      console.log('Showing toast:', message, type);
      setNotification({ message, type });
  }, []);

  const handleCloseToast = useCallback(() => {
      setNotification(null);
  }, []);
  
  const cartItem = cart.find(item => item.id === product.id);
  const quantityInCart = cartItem ? cartItem.quantity : 0;

  const gallery = product.images || [product.image];

  const handleShare = async () => {
    console.log('Handle share clicked');
    const longUrl = `${window.location.origin}${window.location.pathname}?p=${product.id}`;
    let shareUrl = longUrl;

    try {
      const baseUrl = (process.env.REACT_APP_API_ENDPOINT || '').replace(/\/$/, '');
      const res = await fetch(`${baseUrl}/shorten`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetUrl: longUrl })
      });
      const data = await res.json();
      if (data.shortUrl) shareUrl = data.shortUrl;
    } catch (err) {
      console.error('Shorten error:', err);
    }

    // 1. Try Native Share API (Ideal for Mobile)
    if (navigator.share) {
      try {
        await navigator.share({
          title: product.name,
          text: `Check out ${product.name} at Piggy Dental`,
          url: shareUrl,
        });
        // On success, mobile browsers usually show their own confirmation, 
        // but we can show a small toast just in case.
        showToast('Link ready to share!', 'success');
        return;
      } catch (err: any) {
        if (err.name === 'AbortError') return; // User cancelled
        console.error('Native share failed:', err);
      }
    }

    // 2. Fallback to Clipboard (Desktop or if Share API fails)
    try {
      await navigator.clipboard.writeText(shareUrl);
      showToast('Copied! Share this link with friends: ' + shareUrl, 'success');
    } catch (err) {
      console.error('Clipboard error:', err);
      showToast('Unable to copy. Please share the URL from your browser address bar.', 'error');
    }
  };

  const handlePrev = () => {
    if (activeMedia === 'video') {
        setActiveMedia(gallery.length - 1);
    } else {
        const idx = activeMedia as number;
        if (idx === 0) {
            setActiveMedia(product.video ? 'video' : gallery.length - 1);
        } else {
            setActiveMedia(idx - 1);
        }
    }
  };

  const handleNext = () => {
    if (activeMedia === 'video') {
        setActiveMedia(0);
    } else {
        const idx = activeMedia as number;
        if (idx === gallery.length - 1) {
            setActiveMedia(product.video ? 'video' : 0);
        } else {
            setActiveMedia(idx + 1);
        }
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <button
          onClick={() => onNavigate(`catalog-${product.category.toLowerCase()}`)}
          className="mb-8 text-blue-600 hover:text-blue-700 font-semibold flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
          Back to {product.category === 'LAB' ? 'Laboratory' : 'Clinical'} Products
        </button>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Media Section */}
          <div className="space-y-6">
            <div className="aspect-square bg-white rounded-3xl shadow-xl overflow-hidden border border-slate-100 relative group">
                {activeMedia === 'video' && product.video ? (
                    <video src={product.video} className="w-full h-full object-contain bg-black" controls autoPlay />
                ) : (
                    <img 
                        src={gallery[activeMedia as number] || product.image} 
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                )}
                
                {/* Navigation Arrows */}
                <button 
                    onClick={(e) => { e.stopPropagation(); handlePrev(); }}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center text-slate-800 shadow-lg hover:bg-white hover:scale-110 transition-all opacity-0 group-hover:opacity-100"
                >
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                </button>
                <button 
                    onClick={(e) => { e.stopPropagation(); handleNext(); }}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center text-slate-800 shadow-lg hover:bg-white hover:scale-110 transition-all opacity-0 group-hover:opacity-100"
                >
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                </button>

                {product.onPromotion && (
                    <div className="absolute top-6 left-6 bg-red-600 text-white px-4 py-2 rounded-2xl font-black uppercase tracking-widest shadow-xl animate-bounce">
                        Save {product.discount}%
                    </div>
                )}
            </div>

            {/* Gallery Thumbnails */}
            <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide">
                {product.video && (
                    <button 
                        onClick={() => setActiveMedia('video')}
                        className={`w-24 h-24 rounded-2xl border-2 overflow-hidden flex-shrink-0 transition-all relative ${activeMedia === 'video' ? 'border-blue-600 scale-95 shadow-inner' : 'border-transparent shadow-sm'}`}
                    >
                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center text-white text-2xl">▶️</div>
                        <video src={product.video} className="w-full h-full object-cover" />
                    </button>
                )}
                {gallery.map((img, idx) => (
                    <button 
                        key={idx}
                        onClick={() => setActiveMedia(idx)}
                        className={`w-24 h-24 rounded-2xl border-2 overflow-hidden flex-shrink-0 transition-all ${activeMedia === idx ? 'border-blue-600 scale-95 shadow-inner' : 'border-transparent shadow-sm'}`}
                    >
                        <img src={img} className="w-full h-full object-cover" alt="" />
                    </button>
                ))}
            </div>
          </div>

          {/* Info Section */}
          <div className="flex flex-col">
            <div className="bg-white rounded-[2.5rem] p-10 shadow-xl border border-slate-100 flex-1">
                <div className="mb-8">
                    <span className="text-[10px] font-black uppercase tracking-[0.2em] text-blue-600 mb-2 block">Premium Dental Supply</span>
                    <h1 className="text-5xl font-black text-slate-900 mb-4 tracking-tight">{product.name}</h1>
                    <div className="flex items-center gap-4">
                        <span className="text-4xl font-black text-slate-900">${product.price.toFixed(2)}</span>
                        {product.onPromotion && (
                            <span className="text-xl text-slate-400 line-through font-bold">${(product.price / (1 - (product.discount || 0)/100)).toFixed(2)}</span>
                        )}
                    </div>
                </div>

                <div className="flex flex-wrap gap-3 mb-8">
                    <span className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest ${
                        product.stock > 10 ? 'bg-green-50 text-green-600' : 
                        product.stock > 0 ? 'bg-orange-50 text-orange-600' : 
                        'bg-red-50 text-red-600'
                    }`}>
                        {product.stock > 10 ? 'In Stock' : product.stock > 0 ? `Limited Stock (${product.stock})` : 'Sold Out'}
                    </span>
                    <span className="bg-blue-50 text-blue-600 px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest">
                        {product.category} Series
                    </span>
                    {quantityInCart > 0 && (
                        <span className="bg-blue-600 text-white px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest flex items-center gap-2 shadow-lg shadow-blue-100">
                            <span>🛒</span> {quantityInCart} Units in Cart
                        </span>
                    )}
                </div>

                <div className="mb-10">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-xs font-black uppercase text-slate-400 tracking-widest">Specifications</h3>
                        <button 
                            onClick={handleShare}
                            className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-blue-600 hover:text-blue-700 transition-colors"
                        >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" /></svg>
                            Share Item
                        </button>
                    </div>
                    <p className="text-slate-600 leading-relaxed text-lg font-medium mb-10">{product.description}</p>

                    {product.documents && product.documents.length > 0 && (
                        <div className="animate-fade-in">
                            <h3 className="text-xs font-black uppercase text-slate-400 tracking-widest mb-4">Technical Resources</h3>
                            <div className="grid gap-3">
                                {product.documents.map((doc, idx) => (
                                    <a 
                                        key={idx} 
                                        href={doc.url} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl border border-slate-100 hover:bg-blue-50 hover:border-blue-100 transition-all group"
                                    >
                                        <div className="flex items-center gap-3">
                                            <span className="text-2xl group-hover:scale-110 transition-transform">📄</span>
                                            <div>
                                                <div className="text-xs font-black text-slate-900 uppercase tracking-tight">{doc.name}</div>
                                                <div className="text-[9px] font-black text-slate-400 uppercase tracking-[0.1em]">PDF Resource • Download</div>
                                            </div>
                                        </div>
                                        <svg className="w-5 h-5 text-slate-300 group-hover:text-blue-600 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a2 2 0 002 2h12a2 2 0 002-2v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                                    </a>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div className="flex flex-col sm:flex-row gap-4 mt-auto">
                    <button
                        onClick={() => {
                            const success = addToCart(product);
                            if (!success) {
                                showToast(`Only ${product.stock} items available in stock.`, 'error');
                            } else {
                                showToast('Added to cart', 'success');
                            }
                        }}
                        disabled={product.stock === 0}
                        className="flex-1 px-10 py-6 bg-slate-900 text-white rounded-2xl hover:bg-black disabled:bg-slate-200 disabled:text-slate-400 transition-all font-black text-sm uppercase tracking-[0.3em] shadow-2xl hover:-translate-y-1 active:scale-95"
                    >
                        {product.stock === 0 ? 'Join Waitlist' : 'Add to Purchase'}
                    </button>
                    <button
                        onClick={() => onNavigate('cart')}
                        className="px-10 py-6 border-2 border-slate-200 text-slate-900 rounded-2xl hover:bg-slate-50 transition-all font-black text-sm uppercase tracking-[0.2em] active:scale-95"
                    >
                        Review Cart
                    </button>
                </div>
            </div>
            
            <div className="mt-8 flex items-center justify-between px-6">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center text-blue-600">🛡️</div>
                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">FDA Approved Supplies</span>
                </div>
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center text-blue-600">🚚</div>
                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Express Delivery Available</span>
                </div>
            </div>
          </div>
        </div>
        {notification && (
            <Toast 
                message={notification.message} 
                type={notification.type} 
                onClose={handleCloseToast} 
            />
        )}
      </div>
    </div>
  );
};

export default ProductDetail;